import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { initials } from "@/lib/utils";

// Module-level cache of signed URLs so we don't re-fetch every render.
// Signed URLs expire after 1h; refetch happens on the next mount after expiry.
interface CachedUrl { url: string; expires_at: number; }
const urlCache = new Map<string, CachedUrl>();

interface Props {
  path?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  size?: number;
  className?: string;
}

export function EmployeeAvatar({ path, firstName, lastName, email, size = 32, className = "" }: Props) {
  const [url, setUrl] = useState<string | null>(() => {
    if (!path) return null;
    const c = urlCache.get(path);
    return c && c.expires_at > Date.now() ? c.url : null;
  });

  useEffect(() => {
    if (!path) { setUrl(null); return; }
    const c = urlCache.get(path);
    if (c && c.expires_at > Date.now()) { setUrl(c.url); return; }
    let cancelled = false;
    void supabase.storage.from("employee-pics").createSignedUrl(path, 3600).then(({ data }) => {
      if (cancelled) return;
      if (data?.signedUrl) {
        urlCache.set(path, { url: data.signedUrl, expires_at: Date.now() + 3500 * 1000 });
        setUrl(data.signedUrl);
      }
    });
    return () => { cancelled = true; };
  }, [path]);

  const px = `${size}px`;
  const fontSize = size <= 28 ? "10px" : size <= 36 ? "11px" : size <= 48 ? "14px" : `${Math.round(size * 0.36)}px`;

  if (url) {
    return (
      <img
        src={url}
        alt={[firstName, lastName].filter(Boolean).join(" ") || email || "Employee"}
        loading="lazy"
        style={{ width: px, height: px }}
        className={`shrink-0 rounded-full object-cover ${className}`}
      />
    );
  }
  return (
    <span
      style={{ width: px, height: px, fontSize }}
      className={`flex shrink-0 items-center justify-center rounded-full bg-teal-mint font-semibold text-teal-dark ${className}`}
    >
      {initials(firstName ?? null, lastName ?? null, email ?? undefined)}
    </span>
  );
}
